import './utils/pip-polyfill';
import React, { useEffect } from 'react';
import { BrowserRouter, Route } from 'react-router-dom';
import { getSerializedData } from '@jaredpalmer/after';
import { ApolloProvider } from '@apollo/client';
import { CacheProvider } from '@emotion/react';
import createCache from '@emotion/cache';
import { ThemeProvider, CssBaseline } from '@mui/material';
import { StylesProvider, createGenerateClassName } from '@mui/styles';
import { createTheme } from '@mui/material/styles';
import { ThemeProvider as StyledThemeProvider } from 'styled-components';
import createApolloClient from './createApolloClient';
import Layout from './components/Layout';
import theme from './theme';
import { SiteSettingsProvider } from 'context/SiteSettingsContext';
import { DashboardProvider } from 'context/DashboardContext';
import { Cookies, CookiesProvider } from 'react-cookie';
import { SnackbarProvider } from 'notistack';
import initDayJs from 'utils/initDayJs';
import { AuthProvider } from 'context/AuthContext';
import { QueryParamProvider } from 'use-query-params';
import AnalyticsListener from 'components/AnalyticsListener';
import { PictureInPictureProvider } from 'context/PictureInPictureContext';
import { VideoPlayerProvider } from 'context/VideoPlayerContext';
import { DashboardContextsProvider } from './context/Dashboard';
import { TourProvider } from 'context/TourContext';
import { ServerProvider } from 'context/ServerContext/ServerContext';

const emotionCache = createCache({ key: 'mui', prepend: true });
const generateClassName = createGenerateClassName({ productionPrefix: 'j' });

const cookies = new Cookies();
const preloadedState = getSerializedData('preloaded_state');
export const client = createApolloClient({
  ssrForceFetchDelay: 100,
}, null, preloadedState);

const preloadedUserState = getSerializedData('preloaded_user_state') || {};

initDayJs();

function CookieHandler({ children }) {
  if (process.env.NODE_ENV === 'test') {
    return <>{children}</>;
  }

  return (
    <CookiesProvider>
      {children}
    </CookiesProvider>
  )
}

const AuthComponent = ({children, ...props}) => {
  if (process.env.NODE_ENV === 'test') {
    return <>{children}</>;
  }
  return <AuthProvider value={props.value}>{children}</AuthProvider>;
};

const InnerApp = ({ children }) => {
  // Force StylesProvider to re-render on client side as a hack workaround
  // for an SSR issue.
  const [key, setKey] = React.useState(0);

  useEffect(() => {
    setKey(1);
  }, []);

  useEffect(() => {
    const jss = document.querySelector('#jss-server-side');
    if (jss && jss.parentElement) jss.parentElement.removeChild(jss);
  }, []);


  const muiTheme = React.useMemo(
    () =>
      createTheme(theme, {
        components: {
          MuiUseMediaQuery: { defaultProps: { noSsr: true } },
        },
      }),
    []
  );

  return (
    <CacheProvider value={emotionCache}>
      <ThemeProvider theme={muiTheme}>
        <StyledThemeProvider theme={muiTheme}>
          <CssBaseline />
          <StylesProvider key={key} generateClassName={generateClassName}>
            <SnackbarProvider hideIconVariant maxSnack={3}>
              <Layout>
                {children}
              </Layout>
            </SnackbarProvider>
          </StylesProvider>
        </StyledThemeProvider>
      </ThemeProvider>
    </CacheProvider>
  );
}

const App = ({ children }) => {
  return (
    <BrowserRouter>
      <ApolloProvider client={client}>
        <QueryParamProvider ReactRouterRoute={Route}>
          <AuthComponent value={preloadedUserState}>
            <ServerProvider>
              <CookieHandler>
                <SiteSettingsProvider>
                  <AnalyticsListener>
                    <PictureInPictureProvider>
                      <VideoPlayerProvider>
                        <DashboardProvider>
                          <DashboardContextsProvider>
                            <TourProvider>
                              <InnerApp>{children}</InnerApp>
                            </TourProvider>
                          </DashboardContextsProvider>
                        </DashboardProvider>
                      </VideoPlayerProvider>
                    </PictureInPictureProvider>
                  </AnalyticsListener>
                </SiteSettingsProvider>
              </CookieHandler>
            </ServerProvider>
          </AuthComponent>
        </QueryParamProvider>
      </ApolloProvider>
    </BrowserRouter>
  );
};

export default App;
